// src/services/queries/dashboards/tokenHistoryQueries.ts

import { API_BASE_URL } from "@/services/";

// API response structure (parallel arrays)
export interface TokenMetricSeries {
  metric_key: string;
  unit?: string | null;
  dates: number[]; // Array of YYYYMMDD dates
  values: (number | null)[]; // Parallel array of values
}

export interface TokenHistoryResponse {
  ticker: string;
  name?: string | null;
  metrics: TokenMetricSeries[];
}

/**
 * Fetch historical metrics for a specific token
 * Uses the new multi-ticker /tokens/history/points endpoint
 */
export async function fetchTokenHistory(params: {
  tokenSymbol: string;
  metricKeys?: string[];
  signal?: AbortSignal;
}): Promise<TokenHistoryResponse> {
  const metricKeysCsv = params.metricKeys && params.metricKeys.length > 0 
    ? params.metricKeys.join(",") 
    : undefined;
  
  const sp = new URLSearchParams();
  sp.set("tickers", params.tokenSymbol.toUpperCase());
  if (metricKeysCsv) {
    sp.set("metric_keys", metricKeysCsv);
  }
  
  const url = `${API_BASE_URL}/tokens/history/points?${sp.toString()}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  
  // Multi-ticker endpoint returns an array, extract the first (and only) result
  const results = (await res.json()) as TokenHistoryResponse[];
  if (!results || results.length === 0) {
    throw new Error(`No data returned for ticker ${params.tokenSymbol}`);
  }
  return results[0]!;
}

export const tokenHistoryQueryOptions = (tokenSymbol: string, metricKeys?: string[]) => ({
  queryKey: ["dashboard", "token", "history", tokenSymbol, ...(metricKeys || [])],
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const params: { tokenSymbol: string; metricKeys?: string[]; signal?: AbortSignal } = { tokenSymbol };
    if (metricKeys) params.metricKeys = metricKeys;
    if (signal) params.signal = signal;
    return fetchTokenHistory(params);
  },
  staleTime: 1000 * 60 * 10, // 10m
});

